import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Paper, CircularProgress } from '@mui/material';
import ReservationModal from '../components/ReservationModal';
import { useSettings } from '../contexts/SettingsContext';
import { DirectionsBoat as BoatIcon } from '@mui/icons-material';

/**
 * Staff reservation page that uses the same ReservationModal as the public booking page
 * but with staff-specific features like the ability to skip payment
 */
const NewReservationPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { marinaSettings, loading } = useSettings();

  useEffect(() => {
    // Open the modal automatically when the page loads
    const timer = setTimeout(() => {
      setModalOpen(true);
    }, 500); // Short delay to ensure page is fully loaded
    
    return () => clearTimeout(timer);
  }, []);

  const handleCloseModal = () => {
    // When modal is closed, keep the user on the same page
    setModalOpen(false);
    
    // Reopen the modal after a short delay
    setTimeout(() => {
      setModalOpen(true);
    }, 1000);
  };

  return (
    <Container 
      maxWidth={false} 
      disableGutters 
      sx={{ 
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0
      }}
    >
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        width="100%"
      >
        {loading ? (
          <CircularProgress size={60} thickness={4} />
        ) : (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              backgroundColor: 'transparent'
            }}
          >
            <BoatIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Staff Reservation Portal
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create a new reservation for a customer
            </Typography>
            {!modalOpen && (
              <Typography 
                variant="body2" 
                sx={{ cursor: 'pointer', color: 'primary.main', mt: 2 }}
                onClick={() => setModalOpen(true)}
              >
                Click here to start a new reservation
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Reservation Modal with staff mode */}
      <ReservationModal 
        open={modalOpen} 
        onClose={handleCloseModal}
        isStaffMode={true}
      />
    </Container>
  );
};

export default NewReservationPage; 